import React, { useState, useEffect } from 'react';
import axios from 'axios';

/**
 * Reusable Bank Selector Component
 * Shows bank dropdown for non-cash payments
 * @param {string} paymentMode - Current payment mode (Cash, UPI, Online, etc.)
 * @param {string} selectedBank - Currently selected bank ID
 * @param {function} onBankChange - Callback when bank is selected
 * @param {boolean} required - Whether bank selection is required
 */
const BankSelector = ({ paymentMode, selectedBank, onBankChange, required = true }) => {
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchBanks();
  }, []);

  const fetchBanks = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/accounts/banks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBanks(response.data || []);
      
      // Auto-select first bank if none selected
      if (response.data && response.data.length > 0 && !selectedBank) {
        onBankChange(response.data[0].id);
      }
    } catch (error) {
      console.error('Error fetching banks:', error);
    } finally {
      setLoading(false);
    }
  };

  // Only show for non-cash payments
  if (paymentMode === 'Cash') {
    return null;
  }

  return (
    <div className="mb-3">
      <label className="block text-sm font-semibold text-gray-700 mb-2">
        Select Bank {required && <span className="text-red-500">*</span>}
      </label>
      <select
        className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
        value={selectedBank}
        onChange={(e) => onBankChange(e.target.value)}
        required={required}
        disabled={loading}
      >
        <option value="">-- Select Bank --</option>
        {banks.map(bank => (
          <option key={bank.id} value={bank.id}>
            {bank.bank_name} - {bank.account_number}
          </option>
        ))}
      </select>
      {banks.length === 0 && !loading && (
        <p className="text-xs text-red-600 mt-1">
          ⚠️ No banks configured. Please add a bank in Master Settings.
        </p>
      )}
      {loading && (
        <p className="text-xs text-gray-500 mt-1">Loading banks...</p>
      )}
    </div>
  );
};

export default BankSelector;
